// Pure, Jest-safe decision for "should Sentry drop this event?" — NO
// `import.meta` / no static `@sentry/react` import, so it can be unit-tested
// without the env-coupled `sentry.ts`. `beforeSend` delegates here so every
// drop rule has a regression test instead of living in an untestable closure.
import type { ErrorCode } from '@neogeniux/api-contracts';
import { errorCodeMap } from './app/shared/utils/error-message.utils';

export interface SentryDroppableError {
  /** Set by api.service on 401 paths that already forced a logout. */
  isAuthSessionExpired?: boolean;
  /** Backend business error code (createApiError). */
  errorCode?: ErrorCode;
  /** api.service transport code: NETWORK_ERROR / NETWORK_ABORTED / HTTP_*. */
  code?: string;
}

/**
 * True when the exception is EXPECTED noise that must not page as an error:
 *
 * - `isAuthSessionExpired` — the user is being logged out on purpose
 *   (Sentry NEOWEB-4).
 * - transport `NETWORK_ERROR` / `NETWORK_ABORTED` — a dropped/aborted client
 *   connection (axios status 0). The UI already shows "Network error. Please
 *   check your connection."; backend uptime is tracked by health monitors,
 *   not client error noise (Sentry NEOWEB-G, /restaurant/service polling).
 * - a backend `errorCode` the FE already translates + toasts via
 *   `errorCodeMap` — an expected business outcome the operator already saw.
 *
 * Genuine errors (no flag, unknown code, unmapped errorCode) still report.
 */
export function shouldDropSentryEvent(
  ex: SentryDroppableError | undefined | null
): boolean {
  if (!ex) return false;
  if (ex.isAuthSessionExpired) return true;
  if (ex.code === 'NETWORK_ERROR' || ex.code === 'NETWORK_ABORTED') return true;
  if (ex.errorCode && errorCodeMap[ex.errorCode]) return true;
  return false;
}
